import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { DEMO_STATUS_LABEL, PLAN_LABEL, ACCOUNT_STATUS_LABEL, statusBadge, fmtDate } from "@/lib/adminMeta";
import { Inbox, CalendarCheck, CalendarClock, Building2, Ship } from "lucide-react";

const Stat = ({ icon: Icon, label, value, testid }) => (
  <div className="bg-white rounded-2xl border border-border p-6" data-testid={testid}>
    <div className="flex items-center justify-between mb-4">
      <span className="text-xs uppercase tracking-widest text-muted-foreground font-semibold">{label}</span>
      <div className="w-9 h-9 rounded-lg bg-navy/5 flex items-center justify-center"><Icon className="w-4 h-4 text-navy" /></div>
    </div>
    <div className="text-3xl font-bold text-navy">{value}</div>
  </div>
);

export default function AdminOverview() {
  const navigate = useNavigate();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/admin/overview").then(({ data }) => setD(data)).catch(() => setD(false)); }, []);
  return (
    <div className="space-y-8 fade-up" data-testid="admin-overview">
      <h1 className="text-3xl font-bold text-navy">Marine Ad AI Admin</h1>
      <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-5">
        <Stat icon={Inbox} label="New Demo Requests" value={d ? d.new_demo_requests : "-"} testid="stat-new-demos" />
        <Stat icon={CalendarClock} label="Demo Requests This Month" value={d ? d.demo_requests_this_month : "-"} testid="stat-demos-month" />
        <Stat icon={CalendarCheck} label="Demos Scheduled" value={d ? d.demos_scheduled : "-"} testid="stat-demos-scheduled" />
        <Stat icon={Building2} label="Active Dealer Accounts" value={d ? d.active_dealer_accounts : "-"} testid="stat-active-dealers" />
        <Stat icon={Ship} label="Total Active Inventory" value={d ? d.total_active_inventory : "-"} testid="stat-active-inventory" />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <div className="bg-white rounded-2xl border border-border p-6" data-testid="recent-demo-requests">
          <h2 className="text-lg font-bold text-navy mb-4">Recent Demo Requests</h2>
          {d && d.recent_demo_requests?.length ? (
            <div className="divide-y divide-border">
              {d.recent_demo_requests.map((r) => (
                <button key={r.id} onClick={() => navigate(`/admin/demo-requests/${r.id}`)} className="w-full flex items-center justify-between py-3 text-left hover:bg-secondary/50 px-2 rounded-lg" data-testid={`recent-demo-${r.id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold text-navy truncate">{r.name || "Unknown"} {r.dealership_name ? `- ${r.dealership_name}` : ""}</div>
                    <div className="text-xs text-muted-foreground">{fmtDate(r.created_at)} · {r.email}</div>
                  </div>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border shrink-0 ${statusBadge(r.status || "new")}`}>{DEMO_STATUS_LABEL[r.status || "new"]}</span>
                </button>
              ))}
            </div>
          ) : <div className="text-sm text-muted-foreground py-6 text-center">No demo requests yet.</div>}
        </div>

        <div className="bg-white rounded-2xl border border-border p-6" data-testid="recent-dealers">
          <h2 className="text-lg font-bold text-navy mb-4">Recent Dealer Accounts</h2>
          {d && d.recent_dealers?.length ? (
            <div className="divide-y divide-border">
              {d.recent_dealers.map((dl) => (
                <button key={dl.id} onClick={() => navigate(`/admin/dealers/${dl.id}`)} className="w-full flex items-center justify-between py-3 text-left hover:bg-secondary/50 px-2 rounded-lg" data-testid={`recent-dealer-${dl.id}`}>
                  <div className="min-w-0">
                    <div className="font-semibold text-navy truncate">{dl.name || "Unnamed"}</div>
                    <div className="text-xs text-muted-foreground">{PLAN_LABEL[dl.plan] || dl.plan} · {dl.active_inventory} active boats</div>
                  </div>
                  <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-full border shrink-0 ${statusBadge(dl.account_status)}`}>{ACCOUNT_STATUS_LABEL[dl.account_status] || dl.account_status}</span>
                </button>
              ))}
            </div>
          ) : <div className="text-sm text-muted-foreground py-6 text-center">No dealer accounts yet.</div>}
        </div>
      </div>
    </div>
  );
}
